import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Progress } from "./ui/progress";

const SpendingGoals = ({ purchaseHistory, theme }) => {
  // State for goals and active month
  const [goals, setGoals] = useState(() => {
    const savedGoals = localStorage.getItem("guiltGaugeGoals");
    return savedGoals ? JSON.parse(savedGoals) : {
      monthly: {
        amount: 0,
        categories: {
          clothing: 0,
          entertainment: 0,
          food: 0,
          other: 0
        }
      },
      yearly: {
        savings: 0
      }
    };
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedGoals, setEditedGoals] = useState(goals);
  // activeMonth state is kept but not using setActiveMonth for now
  const [activeMonth] = useState(new Date().getMonth());
  
  // Save goals to localStorage
  useEffect(() => {
    localStorage.setItem("guiltGaugeGoals", JSON.stringify(goals));
  }, [goals]);
  
  // Get current month name
  const getCurrentMonthName = (monthIndex) => {
    const months = ["January", "February", "March", "April", "May", "June", 
                    "July", "August", "September", "October", "November", "December"];
    return months[monthIndex];
  };
  
  // Calculate spending for current month
  const getCurrentMonthSpending = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const thisMonthPurchases = purchaseHistory.filter(purchase => {
      const purchaseDate = new Date(purchase.date);
      return purchaseDate.getMonth() === currentMonth && 
             purchaseDate.getFullYear() === currentYear;
    });
    
    return thisMonthPurchases.reduce((total, purchase) => 
      total + parseFloat(purchase.amount || 0), 0);
  };
  
  // Calculate percentage of monthly spending goal
  const getMonthlyProgress = () => {
    if (!goals.monthly.amount) return 0;
    const currentSpending = getCurrentMonthSpending();
    return Math.min(Math.round((currentSpending / goals.monthly.amount) * 100), 100);
  };
  
  // Handle save goals
  const handleSaveGoals = () => {
    setGoals(editedGoals);
    setIsEditing(false);
  };
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };
  
  // Theme-based progress styling
  const progressStyles = {
    mint: "bg-gradient-to-r from-emerald-200 to-emerald-500",
    pink: "bg-gradient-to-r from-pink-200 to-pink-500",
    lavender: "bg-gradient-to-r from-purple-200 to-purple-500"
  };
  
  return (
    <motion.div
      className="bg-white rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Spending Goals</h2>
        {!isEditing ? (
          <motion.button
            onClick={() => setIsEditing(true)}
            className={`px-4 py-2 rounded-xl text-white text-sm ${buttonStyles[theme]}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Edit Goals
          </motion.button>
        ) : (
          <div className="flex space-x-2">
            <motion.button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 rounded-xl text-gray-600 text-sm border border-gray-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </motion.button>
            <motion.button
              onClick={handleSaveGoals}
              className={`px-4 py-2 rounded-xl text-white text-sm ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Save
            </motion.button>
          </div>
        )}
      </div>
      
      {!isEditing ? (
        <>
          {/* Monthly spending goal display */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <div>
                <h3 className="font-bold text-lg">{getCurrentMonthName(activeMonth)} Budget</h3>
                <p className="text-gray-500 text-sm">Monthly spending goal</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold">
                  ${goals.monthly.amount ? goals.monthly.amount.toFixed(2) : "0.00"}
                </div>
                <div className="text-sm text-gray-500">
                  Spent: ${getCurrentMonthSpending().toFixed(2)}
                </div>
              </div>
            </div>
            
            <Progress 
              value={getMonthlyProgress()} 
              className="h-3 bg-gray-200"
              indicatorClass={progressStyles[theme]}
            />
            
            <div className="mt-2 text-right text-sm">
              <span className={getMonthlyProgress() > 90 ? "text-red-500 font-semibold" : "text-gray-600"}>
                {getMonthlyProgress()}% of budget
              </span>
            </div>
          </div>
          
          {/* Budget categories */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {Object.entries(goals.monthly.categories).map(([category, amount]) => (
              <div key={category} className="bg-gray-50 rounded-xl p-3">
                <div className="capitalize font-medium mb-1">{category}</div>
                <div className="text-lg font-bold">${amount ? amount.toFixed(2) : "0.00"}</div>
              </div>
            ))}
          </div>
          
          {/* Yearly savings goal */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-bold">Yearly Savings Goal</h3>
                <p className="text-gray-600 text-sm">Money you want to save this year</p>
              </div>
              <div className="text-2xl font-bold">
                ${goals.yearly.savings ? goals.yearly.savings.toFixed(2) : "0.00"}
              </div>
            </div>
          </div>
          
          {/* Goal tips */}
          <div className="mt-4 text-center text-sm text-gray-500 italic">
            {!goals.monthly.amount ? 
              "Set your monthly budget to start tracking progress!" : 
              getMonthlyProgress() > 90 ? 
                "You're near your spending limit this month. Time to cool off!" :
                "Stay within budget and watch your savings grow!"
            }
          </div>
        </>
      ) : (
        <>
          {/* Edit form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Monthly Spending Limit</label>
              <div className="relative">
                <span className="absolute left-4 top-3 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editedGoals.monthly.amount || ""}
                  onChange={(e) => setEditedGoals({
                    ...editedGoals,
                    monthly: {
                      ...editedGoals.monthly,
                      amount: parseFloat(e.target.value) || 0
                    }
                  })}
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Set your overall monthly budget</p>
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Category Budgets</label>
              <div className="space-y-2">
                {Object.entries(editedGoals.monthly.categories).map(([category, amount]) => (
                  <div key={category} className="flex items-center">
                    <div className="w-1/3 capitalize">{category}</div>
                    <div className="w-2/3 relative">
                      <span className="absolute left-4 top-3 text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={amount || ""}
                        onChange={(e) => {
                          const updatedCategories = {
                            ...editedGoals.monthly.categories,
                            [category]: parseFloat(e.target.value) || 0
                          };
                          setEditedGoals({
                            ...editedGoals,
                            monthly: {
                              ...editedGoals.monthly,
                              categories: updatedCategories
                            }
                          });
                        }}
                        className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-gray-600 mb-1">Yearly Savings Goal</label>
              <div className="relative">
                <span className="absolute left-4 top-3 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editedGoals.yearly.savings || ""}
                  onChange={(e) => setEditedGoals({
                    ...editedGoals,
                    yearly: {
                      ...editedGoals.yearly,
                      savings: parseFloat(e.target.value) || 0
                    }
                  })}
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">How much do you aim to save this year?</p>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default SpendingGoals;
